import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { Trash2 } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'

import { toast } from 'react-toastify'
import { AuthContext } from '../../context/Auth/AuthContext'
import toastError from '../../errors/toastError'
import { isSuperOrAdmin } from '../../helpers/isSuperOrAdmin'
import api from '../../services/api'
import { ButtonPrimary } from '../ButtonPrimary'
import ConfirmationModal from '../ConfirmationModal'
import ContactCustomFieldModal from '../ContactCustomFieldModal'
import { InputFieldText } from '../InputFieldText'
import { useStyles } from './style'

const ContactCustomFields = ({ contact, onUpdate }) => {
  const classes = useStyles()
  const { user } = useContext(AuthContext)
  const [customFields, setCustomFields] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedField, setSelectedField] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [fieldToDelete, setFieldToDelete] = useState(null)
  // const [, setLoading] = useState(false)
  const [loadingFieldId, setLoadingFieldId] = useState(null)

  // Verificar se o usuário tem permissão para criar campos customizados
  const canCreateCustomFields = isSuperOrAdmin(user)

  useEffect(() => {
    setCustomFields([])
    if (contact?.extraInfo) {
      setCustomFields(contact.extraInfo)
      console.log('Campos customizados carregados do contato:', contact.extraInfo)
    }
  }, [contact?.id])

  // const fetchCustomFields = async () => {
  //   if (!contact?.id) return

  //   try {
  //     setLoading(true)
  //     const { data } = await api.get(`/contacts/${contact.id}/custom-fields`)
  //     setCustomFields(data)
  //   } catch (err) {
  //     toastError(err)
  //   } finally {
  //     setLoading(false)
  //   }
  // }

  const handleAddField = () => {
    setSelectedField(null)
    setModalOpen(true)
  }

  // const handleEditField = field => {
  //   setSelectedField(field)
  //   setModalOpen(true)
  // }

  const handleDeleteField = field => {
    setFieldToDelete(field)
    setConfirmModalOpen(true)
  }

  const confirmDeleteField = async () => {
    if (!fieldToDelete) return
    try {
    await api.delete(
        `/contacts/${contact.id}/custom-fields/${fieldToDelete.id}`
      )
      // const deletedCount = response.data?.deletedCount || 1
      toast.success(
        `Campo customizado "${fieldToDelete.name}" removido com sucesso!`
      )
      setCustomFields(prev =>
        prev.filter(field => field.name !== fieldToDelete.name)
      )
    } catch (err) {
      toastError(err)
    } finally {
      setConfirmModalOpen(false)
      setFieldToDelete(null)
    }
  }

  const handleModalSave = async (savedField, action) => {
    if (action === 'create') {
      setCustomFields(prev => [...prev, savedField])
    } else if (action === 'update') {
      setCustomFields(prev =>
        prev.map(field => (field.id === savedField.id ? savedField : field))
      )
    }
  }

  const handleUpdateName = async (field, newName) => {
    const nameTrimmed = newName?.trim()
    if (!nameTrimmed || field.name === nameTrimmed) return

    try {
      setLoadingFieldId(field.id)
      // Enviamos o novo nome para a mesma rota de PUT
      const { data } = await api.put(
        `/contacts/${contact.id}/custom-fields/${field.id}`,
        { name: nameTrimmed }
      )

      setCustomFields(prev => prev.map(f => (f.id === field.id ? data : f)))
      toast.success('Nome do campo atualizado!')
    } catch (err) {
      toastError(err)
    } finally {
      setLoadingFieldId(null)
    }
  }

  // Função para salvar a edição feita diretamente no card
  const handleUpdateValue = async (field, newValue) => {
    const valueTrimmed = newValue?.trim()
    if (field.value === valueTrimmed) return

    try {
      setLoadingFieldId(field.id)
      const { data } = await api.put(
        `/contacts/${contact.id}/custom-fields/${field.id}`,
        { value: valueTrimmed }
      )

      setCustomFields(prev => prev.map(f => (f.id === field.id ? data : f)))
      toast.success('Valor do campo atualizado!')
    } catch (err) {
      toastError(err)
    } finally {
      setLoadingFieldId(null)
    }
  }

  return (
    <div className={classes.customFieldsContainer}>
      <Box className={classes.sectionContent}>
        {canCreateCustomFields && (
          <>
            <Typography component="span" className={classes.sectionTitle}>
              Campos Customizados
            </Typography>
            <ButtonPrimary
              label="Adicionar Campo"
              size="small"
              onClick={handleAddField}
              className={classes.addButton}
            />
          </>
        )}
      </Box>

      {customFields.length === 0 ? (
        <Typography className={classes.noFieldsMessage}>
          {canCreateCustomFields
            ? 'Nenhum campo customizado adicionado'
            : 'Nenhum campo customizado disponível.'}
        </Typography>
      ) : (
        customFields.map(field => (
          <div key={field.id} className={classes.customFieldBox}>
            <div className={classes.customFieldContent}>
              <TextField
                key={`name-${field.id}`}
                name={field.name}
                defaultValue={field.name}
                placeholder="Nome do campo"
                disabled={loadingFieldId === field.id || !canCreateCustomFields}
                // InputProps={{
                //   disableUnderline: true
                // }}
                className={classes.fieldName}
                onBlur={e => handleUpdateName(field, e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.target.blur()
                  }
                }}
              />

              <InputFieldText
                key={`value-${field.id}`}
                defaultValue={field.value}
                placeholder="Digite o valor"
                name={field.name}
                disabled={loadingFieldId === field.id}
                // disableUnderline={false}
                onBlur={e => handleUpdateValue(field, e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    e.target.blur()
                  }
                }}
              />
            </div>
            <div className={classes.customFieldActions}>
              {canCreateCustomFields && (
                <Tooltip title="Remover campo para todos">
                  <IconButton
                    size="small"
                    className={classes.iconButtonDelete}
                    onClick={() => handleDeleteField(field)}
                  >
                    <Trash2 size={18} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        ))
      )}

      {/* Modais mantidos conforme sua implementação original */}
      <ContactCustomFieldModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        contactId={contact?.id}
        customField={selectedField}
        onSave={handleModalSave}
      />

      <ConfirmationModal
        title="Remover Campo Customizado"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={confirmDeleteField}
      >
        <div>
          <p>
            Tem certeza que deseja remover o campo "
            <strong>{fieldToDelete?.name}</strong>"?
          </p>
          <p
            style={{ color: '#f44336', fontWeight: 'bold', marginTop: '16px' }}
          >
            ⚠️ ATENÇÃO: Esta ação afetará todos os contatos.
          </p>
        </div>
      </ConfirmationModal>
    </div>
  )
}

export default ContactCustomFields
